import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import {
  Toolbar as AriaToolbar,
  Separator as AriaSeparator,
  composeRenderProps,
  ToolbarProps,
  SeparatorProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';

const toolbarVariants = cva([
  'flex items-center gap-2 border-b border-neutral/10 p-4',
  /* Orientation */
  'data-[orientation=vertical]:flex-col',
]);

const floatingToolbarVariants = cva(
  [
    // z-index is 9999 to ensure it is on top of the data grid's horizontal scrollbar
    'fixed z-[9999] flex items-center gap-2 rounded-md border border-neutral/20 bg-white p-2 shadow-lg',
    /* Orientation */
    'data-[orientation=vertical]:flex-col',
  ],
  {
    variants: {
      position: {
        top: 'top-6 left-1/2 -translate-x-1/2',
        right: 'top-1/2 right-6 -translate-y-1/2',
        bottom: 'bottom-6 left-1/2 -translate-x-1/2',
        left: 'top-1/2 left-6 -translate-y-1/2',
      },
      visible: {
        false: 'hidden',
        true: '',
      },
    },
    defaultVariants: {
      position: 'bottom',
    },
  },
);

const separatorVariants = cva('', {
  variants: {
    divider: {
      true: 'border-neutral/20',
      false: 'border-transparent',
    },
  },
});

/**
 * Toolbar component for grouping related controls.
 * Provides a container for buttons, toggles, and other interactive elements.
 * Supports both horizontal and vertical orientations.
 *
 * @example
 * ```tsx
 * <Toolbar aria-label="Text formatting">
 *   <Button>Bold</Button>
 *   <Button>Italic</Button>
 *   <Separator orientation="vertical" />
 *   <Button>Underline</Button>
 * </Toolbar>
 * ```
 *
 * @param props - Toolbar properties
 */
export const Toolbar = (props: ToolbarProps) => (
  <AriaToolbar
    {...props}
    className={composeRenderProps(props.className, (className) =>
      cn('@Toolbar', toolbarVariants(), className),
    )}
  />
);

/**
 * Props for the FloatingToolbar component.
 */
interface FloatingToolbarProps
  extends
    Omit<ToolbarProps, 'orientation'>,
    VariantProps<typeof floatingToolbarVariants> {
  /**
   * Position of the floating toolbar relative to the viewport
   * @default 'bottom'
   */
  position?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * Whether the toolbar is visible or not.
   * @default false
   */
  visible?: boolean;
}

/**
 * Floating toolbar that appears fixed on the screen.
 * Useful for providing contextual tools that should remain accessible.
 * Automatically sets orientation based on position.
 *
 * @example
 * ```tsx
 * <FloatingToolbar position="bottom" aria-label="Formatting options">
 *   <Button>Format</Button>
 *   <Button>Copy</Button>
 * </FloatingToolbar>
 * ```
 *
 * @param props - FloatingToolbar properties
 */
export const FloatingToolbar = ({
  position = 'bottom',
  visible = false,
  children,
  ...props
}: FloatingToolbarProps) => (
  <Toolbar
    {...props}
    className={composeRenderProps(props.className, (className) =>
      cn(
        '@FloatingToolbar',
        floatingToolbarVariants({ position, visible }),
        className,
      ),
    )}
    orientation={
      position === 'left' || position === 'right' ? 'vertical' : 'horizontal'
    }
  >
    {children}
  </Toolbar>
);

/**
 * Separator component for visually dividing toolbar items.
 * Renders a line that adapts to the toolbar's orientation.
 *
 * @example
 * ```tsx
 * <Toolbar>
 *   <Button>Cut</Button>
 *   <Button>Copy</Button>
 *   <Separator orientation="vertical" />
 *   <Button>Paste</Button>
 * </Toolbar>
 * ```
 *
 * @param props - Separator properties
 */
export const Separator = (
  props: SeparatorProps & { hideDivider?: boolean },
) => (
  <AriaSeparator
    className={cn(
      '@Separator',
      separatorVariants({ divider: !props.hideDivider }),
      props.className,
    )}
    {...props}
  />
);

/**
 * Spacer component for creating flexible space in a toolbar.
 * Pushes items apart by taking up available space.
 *
 * @example
 * ```tsx
 * <Toolbar>
 *   <Button>Save</Button>
 *   <Spacer />
 *   <Button>Cancel</Button>
 * </Toolbar>
 * ```
 */
export const Spacer = () => <div className="@Spacer grow" />;
